import React, { useState } from "react";
import {
  Controller,
  type UseFormRegister,
  type FieldErrors,
  type Control,
} from "react-hook-form";
import type { Project, ProjectCreate, ProjectUpdate } from "@/type-gen/api";
import { ClientAutoComplete } from "@/shared/components/clientAutoComplete";

export interface ProjectFormData {
  name: string;
  description?: string;
  location?: string;
  shoot_date?: string;
  call_time?: string;
  start_time?: string;
  end_time?: string;
  client_id?: string;
  new_client_name?: string;
}

interface ProjectFormProps {
  register: UseFormRegister<ProjectFormData>;
  control: Control<ProjectFormData>;
  errors: FieldErrors<ProjectFormData>;
  className?: string;
  /** When provided, client selection is hidden. Omit to show client selector. */
  clientId?: string | undefined;
}

export const ProjectForm: React.FC<ProjectFormProps> = ({
  register,
  control,
  errors,
  className = "",
  clientId,
}) => {
  // Internal state for client display name (not stored in form, only for display)
  const [clientDisplayName, setClientDisplayName] = useState<string | null>(
    null,
  );

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Row 1: Project Name */}
      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Project Name *
        </label>
        <input
          type="text"
          {...register("name", {
            required: "Project name is required",
            minLength: {
              value: 1,
              message: "Project name must be at least 1 character",
            },
            maxLength: {
              value: 255,
              message: "Project name must be less than 255 characters",
            },
          })}
          className="w-full px-4 py-2 bg-card border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          placeholder="Major Brand Commercial"
          autoFocus
        />
        {errors.name && (
          <p className="text-xs text-destructive mt-1">{errors.name.message}</p>
        )}
      </div>
      {/* Client Selection - only show when clientId is not provided */}
      {!clientId && (
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Client *
          </label>
          <Controller
            name="client_id"
            control={control}
            render={({ field: clientIdField }) => (
              <Controller
                name="new_client_name"
                control={control}
                render={({ field: newClientNameField }) => {
                  const handleClientChange = (
                    clientId: string | null,
                    clientName: string | null,
                  ) => {
                    if (clientId) {
                      // Existing client selected
                      clientIdField.onChange(clientId);
                      newClientNameField.onChange("");
                      setClientDisplayName(clientName);
                    } else {
                      // Selection cleared
                      clientIdField.onChange(null);
                      newClientNameField.onChange("");
                      setClientDisplayName(null);
                    }
                  };

                  const handleNewClientNameChange = (name: string) => {
                    newClientNameField.onChange(name);
                    if (name) {
                      // Clear client_id when typing new client name
                      clientIdField.onChange(null);
                      setClientDisplayName(null);
                    }
                  };

                  return (
                    <ClientAutoComplete
                      value={clientIdField.value || null}
                      displayValue={clientDisplayName || null}
                      onChange={handleClientChange}
                      onNewClientNameChange={handleNewClientNameChange}
                      newClientName={newClientNameField.value || ""}
                      error={
                        errors.client_id?.message ||
                        (errors.new_client_name?.message as string | undefined)
                      }
                    />
                  );
                }}
              />
            )}
          />
        </div>
      )}

      {/* Row 2: Shoot Address */}
      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Shoot Address
        </label>
        <input
          type="text"
          {...register("location")}
          className="w-full px-4 py-2 bg-card border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          placeholder="Full address for crew directions"
        />
        {errors.location && (
          <p className="text-xs text-destructive mt-1">
            {errors.location.message}
          </p>
        )}
      </div>

      {/* Row 3: Location Type */}
      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Location Type
        </label>
        <input
          type="text"
          {...register("description")}
          className="w-full px-4 py-2 bg-card border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          placeholder="Studio with controlled lighting setup"
        />
        {errors.description && (
          <p className="text-xs text-destructive mt-1">
            {errors.description.message}
          </p>
        )}
      </div>

      {/* Row 4: Shoot Date, Call Time */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Shoot Date
          </label>
          <input
            type="date"
            {...register("shoot_date")}
            className="w-full px-4 py-2 bg-card border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
          {errors.shoot_date && (
            <p className="text-xs text-destructive mt-1">
              {errors.shoot_date.message}
            </p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Call Time
          </label>
          <input
            type="time"
            {...register("call_time")}
            className="w-full px-4 py-2 bg-card border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            placeholder="--:--"
          />
          {errors.call_time && (
            <p className="text-xs text-destructive mt-1">
              {errors.call_time.message}
            </p>
          )}
        </div>
      </div>

      {/* Row 5: Start Time, End Time */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Start Time
          </label>
          <input
            type="time"
            {...register("start_time")}
            className="w-full px-4 py-2 bg-card border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            placeholder="--:--"
          />
          {errors.start_time && (
            <p className="text-xs text-destructive mt-1">
              {errors.start_time.message}
            </p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            End Time
          </label>
          <input
            type="time"
            {...register("end_time")}
            className="w-full px-4 py-2 bg-card border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            placeholder="--:--"
          />
          {errors.end_time && (
            <p className="text-xs text-destructive mt-1">
              {errors.end_time.message}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// Helper function to convert Project to ProjectFormData
export function projectToFormData(project: Partial<Project>): ProjectFormData {
  const shootDate = project.shoot_date
    ? new Date(project.shoot_date).toISOString().split("T")[0]
    : undefined;
  return {
    name: project.name || "",
    ...(project.description != null &&
      project.description !== "" && { description: project.description }),
    ...(project.location != null &&
      project.location !== "" && { location: project.location }),
    ...(shootDate !== undefined && { shoot_date: shootDate }),
    ...(project.call_time != null &&
      project.call_time !== "" && { call_time: project.call_time }),
    ...(project.start_time != null &&
      project.start_time !== "" && { start_time: project.start_time }),
    ...(project.end_time != null &&
      project.end_time !== "" && { end_time: project.end_time }),
  };
}

// Helper function to convert ProjectFormData to ProjectCreate
export function formDataToProjectCreate(
  data: ProjectFormData,
  clientId?: string,
): ProjectCreate {
  return {
    name: data.name.trim(),
    description: data.description?.trim() || null,
    location: data.location?.trim() || null,
    shoot_date: data.shoot_date?.trim() || null,
    call_time: data.call_time?.trim() || null,
    start_time: data.start_time?.trim() || null,
    end_time: data.end_time?.trim() || null,
    client_id: clientId ?? data.client_id ?? null,
    new_client_name: data.new_client_name?.trim() || null,
  };
}

// Helper function to convert ProjectFormData to ProjectUpdate
export function formDataToProjectUpdate(data: ProjectFormData): ProjectUpdate {
  return {
    name: data.name.trim(),
    description: data.description?.trim() || null,
    location: data.location?.trim() || null,
    shoot_date: data.shoot_date?.trim() || null,
    call_time: data.call_time?.trim() || null,
    start_time: data.start_time?.trim() || null,
    end_time: data.end_time?.trim() || null,
  };
}
